import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Search, ToggleLeft, ToggleRight, Tags, Columns, GripVertical, Save, DollarSign, ArrowUp, ArrowDown, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePermission } from "@/hooks/usePermission";

const CATALOG_TYPES = [
  { key: "cliente_ac", label: "Clientes" },
  { key: "solicitante_ac", label: "Solicitantes" },
  { key: "forma_pago_ac", label: "Formas de Pago" },
  { key: "area_ac", label: "Áreas" },
  { key: "tipo_trabajo_ac", label: "Tipos de Trabajo" },
  { key: "requerimientos_ot_ac", label: "Requerimientos de OT" },
  { key: "autorizacion_ac", label: "Autorización" },
  { key: "tipo_ot_ac", label: "Tipos de OT" },
  { key: "precios_tipo_trabajo_ac", label: "Precios x Trabajo" },
];

const CATALOG_SECTION = "catalogos";
const COLUMNAS_SECTION = "columnas";

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

/* ─── Columnas Config Editor ─── */
function ColumnasEditor({ canEdit }) {
  const qc = useQueryClient();
  const { data: configs = [], isLoading } = useQuery({
    queryKey: ["columnas-partidas-ac"],
    queryFn: async () => (await api.get("/ots-comercial/columnas-partidas")).data,
    staleTime: 30000,
  });
  const [activeType, setActiveType] = useState(null);
  const [editCols, setEditCols] = useState([]);
  const [saving, setSaving] = useState(false);

  const startEdit = useCallback((cfg) => {
    setActiveType(cfg);
    setEditCols(JSON.parse(JSON.stringify(cfg.columnas || [])));
  }, []);

  const cancelEdit = () => { setActiveType(null); setEditCols([]); };

  const updateCol = (idx, field, val) => {
    const copy = [...editCols];
    copy[idx] = { ...copy[idx], [field]: val };
    setEditCols(copy);
  };

  const addCol = () => {
    setEditCols([...editCols, { key: "", label: "", tipo: "text" }]);
  };

  const removeCol = (idx) => {
    setEditCols(editCols.filter((_, i) => i !== idx));
  };

  const moveCol = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= editCols.length) return;
    const copy = [...editCols];
    [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
    setEditCols(copy);
  };

  const saveColumns = async () => {
    if (!activeType) return;
    const cleaned = editCols.filter(c => c.key?.trim() && c.label?.trim());
    if (cleaned.length === 0) return toast.error("Debe haber al menos una columna");
    setSaving(true);
    try {
      await api.patch(`/ots-comercial/columnas-partidas/${activeType.id}`, { columnas: cleaned });
      toast.success(`Columnas de "${activeType.tipo_ot}" actualizadas`);
      qc.invalidateQueries({ queryKey: ["columnas-partidas-ac"] });
      setActiveType(null);
      setEditCols([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar columnas");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>;

  return (
    <div className="space-y-4" data-testid="columnas-editor">
      <p className="text-sm text-muted-foreground">
        Configura qué columnas aparecen en la tabla de partidas para cada tipo de OT.
        Los cambios solo afectan la captura de nuevas OTs; los datos existentes se conservan.
      </p>

      {/* List of type configs */}
      <div className="grid gap-3">
        {configs.map(cfg => (
          <Card key={cfg.id} className="bg-card border-border" data-testid={`columnas-config-${cfg.tipo_ot}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">{cfg.tipo_ot}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {(cfg.columnas || []).length} columnas: {(cfg.columnas || []).map(c => c.label).join(", ")}
                  </p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => startEdit(cfg)} data-testid={`columnas-edit-${cfg.tipo_ot}`}>
                    <Edit className="h-3.5 w-3.5 mr-1" /> Editar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!activeType} onOpenChange={(open) => { if (!open) cancelEdit(); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Columnas de "{activeType?.tipo_ot}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground mb-2">
              <strong>Key</strong> = identificador interno (sin espacios, minúsculas). <strong>Label</strong> = nombre visible. <strong>Tipo</strong> = text o number.
            </div>
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 px-2 w-8">#</th>
                  <th className="py-2 px-2 w-16">Orden</th>
                  <th className="py-2 px-2">Key</th>
                  <th className="py-2 px-2">Label</th>
                  <th className="py-2 px-2 w-28">Tipo</th>
                  <th className="py-2 px-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {editCols.map((col, idx) => (
                  <tr key={idx} className="border-t border-border/60">
                    <td className="py-1.5 px-2 text-muted-foreground font-mono text-xs">{idx + 1}</td>
                    <td className="py-1.5 px-2">
                      <div className="flex gap-0.5">
                        <Button type="button" size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0} onClick={() => moveCol(idx, -1)} data-testid={`col-up-${idx}`}>
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" className="h-6 w-6" disabled={idx === editCols.length - 1} onClick={() => moveCol(idx, 1)} data-testid={`col-down-${idx}`}>
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                    <td className="py-1.5 px-2">
                      <Input className="h-8 text-xs font-mono" value={col.key} onChange={(e) => updateCol(idx, "key", e.target.value.toLowerCase().replace(/\s+/g, "_"))} placeholder="ej: sku" data-testid={`col-key-${idx}`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Input className="h-8 text-xs" value={col.label} onChange={(e) => updateCol(idx, "label", e.target.value)} placeholder="ej: SKU" data-testid={`col-label-${idx}`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Select value={col.tipo || "text"} onValueChange={(v) => updateCol(idx, "tipo", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Texto</SelectItem>
                          <SelectItem value="number">Número</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 px-2">
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeCol(idx)} data-testid={`col-remove-${idx}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button type="button" size="sm" variant="outline" onClick={addCol} data-testid="col-add">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar columna
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={cancelEdit}>Cancelar</Button>
            <Button onClick={saveColumns} disabled={saving} data-testid="col-save">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Guardando..." : "Guardar columnas"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Main Catalogs Tab ─── */
export default function OTACCatalogsTab() {
  const qc = useQueryClient();
  const canEdit = usePermission("otac_edit");
  const [mainSection, setMainSection] = useState(CATALOG_SECTION);
  const [activeCat, setActiveCat] = useState("cliente_ac");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formNombre, setFormNombre] = useState("");
  const [formPrecio, setFormPrecio] = useState("");
  const [saving, setSaving] = useState(false);

  const isPreciosCat = activeCat === "precios_tipo_trabajo_ac";

  const { data: allItems = [], isLoading } = useQuery({
    queryKey: ["catalogs-comercial"],
    queryFn: async () => (await api.get("/ots-comercial/catalogos")).data,
  });

  const items = useMemo(() => {
    let filtered = allItems.filter((i) => i.tipo === activeCat);
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((i) => i.nombre?.toLowerCase().includes(q));
    }
    return filtered;
  }, [allItems, activeCat, search]);

  const counts = useMemo(() => {
    const c = {};
    for (const t of CATALOG_TYPES) c[t.key] = 0;
    for (const item of allItems) {
      if (c[item.tipo] !== undefined) c[item.tipo]++;
    }
    return c;
  }, [allItems]);

  const openCreate = () => {
    setEditItem(null);
    setFormNombre("");
    setFormPrecio("");
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormNombre(item.nombre);
    setFormPrecio(item.precio !== undefined ? String(item.precio) : "");
    setDialogOpen(true);
  };

  const save = async () => {
    if (!formNombre.trim()) return toast.error("El nombre es requerido");
    setSaving(true);
    try {
      const payload = { nombre: formNombre.trim() };
      if (isPreciosCat) {
        payload.precio = parseFloat(formPrecio) || 0;
      }
      if (editItem) {
        await api.patch(`/ots-comercial/catalogos/${editItem.id}`, payload);
        toast.success("Actualizado");
      } else {
        await api.post("/ots-comercial/catalogos", { tipo: activeCat, ...payload });
        toast.success("Agregado");
      }
      setDialogOpen(false);
      qc.invalidateQueries({ queryKey: ["catalogs-comercial"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/ots-comercial/catalogos/${id}`);
      toast.success("Eliminado");
      qc.invalidateQueries({ queryKey: ["catalogs-comercial"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const toggleActivo = async (item) => {
    try {
      await api.patch(`/ots-comercial/catalogos/${item.id}`, { activo: !item.activo });
      toast.success(item.activo ? "Desactivado" : "Activado");
      qc.invalidateQueries({ queryKey: ["catalogs-comercial"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const activeLabel = CATALOG_TYPES.find((t) => t.key === activeCat)?.label || "";

  return (
    <div className="space-y-4" data-testid="otac-catalogs-tab">
      {/* Main section toggle: Catálogos vs Columnas */}
      <Tabs value={mainSection} onValueChange={setMainSection}>
        <TabsList className="h-auto">
          <TabsTrigger value={CATALOG_SECTION} className="text-xs gap-1.5" data-testid="section-catalogos">
            <Tags className="h-3.5 w-3.5" /> Catálogos
          </TabsTrigger>
          <TabsTrigger value={COLUMNAS_SECTION} className="text-xs gap-1.5" data-testid="section-columnas">
            <Columns className="h-3.5 w-3.5" /> Columnas por Tipo de OT
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ─── COLUMNAS SECTION ─── */}
      {mainSection === COLUMNAS_SECTION && <ColumnasEditor canEdit={canEdit} />}

      {/* ─── CATALOGS SECTION ─── */}
      {mainSection === CATALOG_SECTION && (
        <>
          {/* Tabs por tipo de catálogo */}
          <Tabs value={activeCat} onValueChange={(v) => { setActiveCat(v); setSearch(""); }}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              {CATALOG_TYPES.map((t) => (
                <TabsTrigger key={t.key} value={t.key} className="text-xs" data-testid={`otac-cat-tab-${t.key}`}>
                  {t.label}
                  <Badge variant="secondary" className="ml-1.5 text-[10px] py-0 px-1.5">{counts[t.key] || 0}</Badge>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Barra de acciones */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={`Buscar en ${activeLabel}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="otac-cat-search" />
            </div>
            {canEdit && (
              <Button onClick={openCreate} data-testid="otac-cat-add-button">
                <Plus className="h-4 w-4 mr-1" /> Agregar {activeLabel.slice(0, -1) || "valor"}
              </Button>
            )}
          </div>

          {/* Tabla */}
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2.5 px-3">Nombre</th>
                      {isPreciosCat && <th className="py-2.5 px-3 w-32 text-right">Precio</th>}
                      <th className="py-2.5 px-3 w-24">Estado</th>
                      {canEdit && <th className="py-2.5 px-3 w-28">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && [1,2,3].map(i => (
                      <tr key={i} className="border-t border-border/60">
                        <td className="py-2.5 px-3" colSpan={canEdit ? (isPreciosCat ? 4 : 3) : (isPreciosCat ? 3 : 2)}><Skeleton className="h-4 w-full" /></td>
                      </tr>
                    ))}
                    {!isLoading && items.map((item) => (
                      <tr key={item.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`otac-cat-row-${item.id}`}>
                        <td className="py-2.5 px-3 font-medium">{item.nombre}</td>
                        {isPreciosCat && (
                          <td className="py-2.5 px-3 text-right font-mono tabular-nums text-primary font-semibold">
                            {item.precio !== undefined ? fmtMoney(item.precio) : "-"}
                          </td>
                        )}
                        <td className="py-2.5 px-3">
                          {item.activo !== false ? (
                            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/35 text-xs">Activo</Badge>
                          ) : (
                            <Badge className="bg-muted/40 text-muted-foreground border-border text-xs">Inactivo</Badge>
                          )}
                        </td>
                        {canEdit && (
                          <td className="py-2.5 px-3">
                            <div className="flex gap-1">
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleActivo(item)}>
                                      {item.activo !== false ? <ToggleRight className="h-3.5 w-3.5 text-emerald-400" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{item.activo !== false ? "Desactivar" : "Activar"}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(item)} data-testid={`otac-cat-edit-${item.id}`}>
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`otac-cat-delete-${item.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Eliminar "{item.nombre}"?</AlertDialogTitle>
                                    <AlertDialogDescription>Se eliminará del catálogo de {activeLabel}.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => remove(item.id)}>Eliminar</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {!isLoading && items.length === 0 && (
                      <tr>
                        <td colSpan={canEdit ? (isPreciosCat ? 4 : 3) : (isPreciosCat ? 3 : 2)} className="py-12 text-center text-muted-foreground">
                          <Tags className="h-8 w-8 mx-auto mb-2 opacity-40" />
                          {search ? "Sin resultados" : `Sin valores en ${activeLabel}. Agrega el primero.`}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editItem ? "Editar" : "Agregar"} {activeLabel.slice(0, -1) || "valor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Nombre *</Label>
              <Input value={formNombre} onChange={(e) => setFormNombre(e.target.value)} autoFocus data-testid="otac-cat-nombre-input"
                onKeyDown={(e) => e.key === "Enter" && !isPreciosCat && save()} />
            </div>
            {isPreciosCat && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Precio (MXN)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="number" step="0.01" min="0" className="pl-9 font-mono" value={formPrecio} onChange={(e) => setFormPrecio(e.target.value)}
                    data-testid="otac-cat-precio-input" onKeyDown={(e) => e.key === "Enter" && save()} placeholder="0.00" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="otac-cat-save-button">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
